import '@tradeshift/tradeshift-ui';
import './bootstrap';

import * as Sentry from '@sentry/vue';
import mitt from 'mitt';
import { createPinia } from 'pinia';
import PortalVue from 'portal-vue';
import { configureCompat, createApp, defineAsyncComponent } from 'vue';
import { createRouter, createWebHashHistory } from 'vue-router';

import AppRoot from './AppRoot.vue';
import ErrorDialog from './components/Helpers/ErrorDialog.vue';
import SidebarMain from './components/Sidebar/SidebarMain.vue';
import HelpButton from './components/Support/Modals/HelpButton.vue';
import TopBar from './components/TopBar/TopBar';
import { routes } from './vue-routes';

// vue 3 + @vue/compat
configureCompat({
  WATCH_ARRAY: 'suppress-warning',
  RENDER_FUNCTION: false,
  INSTANCE_LISTENERS: 'suppress-warning',
  COMPONENT_FUNCTIONAL: 'suppress-warning',
  OPTIONS_BEFORE_DESTROY: 'suppress-warning',
  INSTANCE_SCOPED_SLOTS: 'suppress-warning',
  OPTIONS_DATA_MERGE: 'suppress-warning',
  COMPONENT_V_MODEL: 'suppress-warning',
  CUSTOM_DIR: 'suppress-warning',
  INSTANCE_EVENT_EMITTER: 'suppress-warning',
  ATTR_FALSE_VALUE: 'suppress-warning',
  INSTANCE_ATTRS_CLASS_STYLE: 'suppress-warning',
  GLOBAL_PROTOTYPE: 'suppress-warning',
  GLOBAL_EXTEND: 'suppress-warning',
  GLOBAL_MOUNT: 'suppress-warning',
  OPTIONS_DESTROYED: 'suppress-warning',
  INSTANCE_DESTROY: 'suppress-warning',
  TRANSITION_GROUP_ROOT: false,
});

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

const app = createApp({});

Sentry.init({
  app,
  dsn: window.env.SENTRY_DSN,
  environment: window.env.SENTRY_ENVIRONMENT,
  release: window.env.SENTRY_RELEASE,
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
    }),
  ],
  tracesSampleRate: 0.0,
  logErrors: true,
});

Sentry.setUser({
  id: window.env.USER_ID,
  // TODO: companyId: window.env.COMPANY_ID,
});

app.use(router);

app.config.compilerOptions.whitespace = 'condense';

export const emitter = mitt();
app.config.globalProperties.$eventBus = emitter; // Global event bus

app.use(PortalVue);

// Common components, dont split:
app.component('AppRoot', AppRoot);
app.component('ErrorDialog', ErrorDialog);
app.component('HelpButton', HelpButton);
app.component('SidebarMain', SidebarMain);
app.component('TopBar', TopBar);

// Only define top level "routes" / components rendered from PHP
app.component(
  'LoginForm',
  defineAsyncComponent(() => import('./components/LoginForm/LoginForm.vue'))
);
app.component(
  'SearchMainComponent',
  defineAsyncComponent(() => import('./components/Search/SearchMain.vue'))
);
app.component(
  'TenantManagementMain',
  defineAsyncComponent(() =>
    import('./components/Support/Tenant/TenantManagement.vue')
  )
);
app.component(
  'TenantsOverviewMain',
  defineAsyncComponent(() =>
    import('./components/Support/Tenant/TenantsOverview.vue')
  )
);
app.component(
  'AdminOverviewMain',
  defineAsyncComponent(() => import('./components/Admin/AdminOverviewMain.vue'))
);
app.component(
  'AdminUserGroupsMain',
  defineAsyncComponent(() =>
    import('./components/Admin/UserGroup/AdminUserGroupsMain.vue')
  )
);
app.component(
  'AdminUserGroupMain',
  defineAsyncComponent(() =>
    import('./components/Admin/UserGroup/AdminUserGroupMain.vue')
  )
);
app.component(
  'AdminUsersMain',
  defineAsyncComponent(() =>
    import('./components/Admin/User/AdminUsersMain.vue')
  )
);
app.component(
  'AdminUserMain',
  defineAsyncComponent(() =>
    import('./components/Admin/User/AdminUserMain.vue')
  )
);
app.component(
  'AdminCustomersMain',
  defineAsyncComponent(() =>
    import('./components/Admin/Customers/AdminCustomersMain.vue')
  )
);
app.component(
  'AdminCustomerMain',
  defineAsyncComponent(() =>
    import('./components/Admin/Customers/AdminCustomerMain.vue')
  )
);
app.component(
  'AdminRolesMain',
  defineAsyncComponent(() =>
    import('./components/Admin/Role/AdminRolesMain.vue')
  )
);
app.component(
  'AdminPassiveMain',
  defineAsyncComponent(() =>
    import('./components/Admin/Passive/AdminPassiveMain.vue')
  )
);
app.component(
  'AdminRoleMain',
  defineAsyncComponent(() =>
    import('./components/Admin/Role/AdminRoleMain.vue')
  )
);
app.component(
  'AuditLogMain',
  defineAsyncComponent(() =>
    import('./components/Support/Audit/AuditLogOverview.vue')
  )
);
app.component(
  'VanAccountsOverviewMain',
  defineAsyncComponent(() =>
    import('./components/PlatformManagement/VanAccountsOverviewMain.vue')
  )
);
app.component(
  'ExternalConnectionDomainsOverviewMain',
  defineAsyncComponent(() =>
    import(
      './components/PlatformManagement/ExternalConnectionDomainsOverviewMain.vue'
    )
  )
);
app.component(
  'SamlAdminMain',
  defineAsyncComponent(() =>
    import('./components/PlatformManagement/SAMLAdminMain.vue')
  )
);
app.component(
  'LegacySamlAdminMain',
  defineAsyncComponent(() =>
    import('./components/PlatformManagement/LegacySAMLAdminMain.vue')
  )
);
app.component(
  'EmailLogs',
  defineAsyncComponent(() =>
    import('./components/PlatformManagement/EmailLogs.vue')
  )
);
app.component(
  'AdminHelpEntriesMain',
  defineAsyncComponent(() =>
    import('./components/Admin/Help/AdminHelpEntriesMain.vue')
  )
);
app.component(
  'AdminHelp',
  defineAsyncComponent(() => import('./components/Admin/Help/AdminHelp.vue'))
);

app.component(
  'AppsVendorList',
  defineAsyncComponent(() =>
    import('./components/PlatformManagement/AppsVendorList.vue')
  )
);
app.component(
  'AppsVendor',
  defineAsyncComponent(() =>
    import('./components/PlatformManagement/AppsVendor.vue')
  )
);
app.component(
  'AppsVendorApp',
  defineAsyncComponent(() =>
    import('./components/PlatformManagement/AppsVendorApp.vue')
  )
);
app.component(
  'EdifierMain',
  defineAsyncComponent(() =>
    import('./components/PlatformManagement/EdifierMain.vue')
  )
);

app.component(
  'BusinessAuditLogTrail',
  defineAsyncComponent(() =>
    import('./components/Admin/BusinessAuditLogTrail.vue')
  )
);

const pinia = createPinia();

app.use(pinia);
app.mount('#app');

// Global Loading Spinner we will show on all page changes
window.onbeforeunload = function () {
  const mainElements = document.querySelectorAll('main');
  if (mainElements.length > 0) {
    const main = mainElements[0];
    main.setAttribute('data-ts.blocking', 'Loading');
  }
};
